/**
 * src/components/shared/OfflineSyncIndicator.jsx — "N waiting to sync" pill.
 *
 * Flag-gated (offline_tasks_v1). Renders a small, non-interactive status pill
 * below the OfflineBanner only when there is something to report (queued edits
 * or an active sync). Honest feedback for the airplane workflow: the CEO always
 * knows whether offline changes are still pending or have flushed.
 */

import { RefreshCw, CloudOff } from 'lucide-react';
import useOfflineSync from '../../hooks/useOfflineSync';
import { useAuth } from '../../auth';
import { isOfflineTasksEnabled } from '../../lib/featureFlags';
import { useT } from '../../lib/i18n/I18nContext';

export default function OfflineSyncIndicator() {
  const { profile } = useAuth();
  const enabled = isOfflineTasksEnabled(profile);
  const { pendingCount, syncing } = useOfflineSync({ enabled });
  const { t } = useT();

  if (!enabled) return null;
  if (!syncing && pendingCount === 0) return null;

  const label = syncing ? t('offline.syncing') : t('offline.pending', { count: pendingCount });

  return (
    <div
      data-testid="offline-sync-indicator"
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 49,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 999,
        background: 'var(--glass)',
        color: 'var(--navy)',
        border: '1px solid var(--surface-line)',
        boxShadow: '0 2px 8px rgba(28, 63, 117, 0.12)',
        fontSize: 12,
        fontWeight: 600,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        pointerEvents: 'none',
      }}
    >
      {syncing ? (
        <RefreshCw size={13} style={{ animation: 'acn-spin 900ms linear infinite' }} aria-hidden="true" />
      ) : (
        <CloudOff size={13} aria-hidden="true" />
      )}
      <span>{label}</span>
      <style>{'@keyframes acn-spin { to { transform: rotate(360deg); } } @media (prefers-reduced-motion: reduce) { [data-testid="offline-sync-indicator"] svg { animation: none !important; } }'}</style>
    </div>
  );
}
